import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { MagnifyingGlass, Sparkle, FileText, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";

export default function CommandPalette({ open, onClose }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState("");
  const [matches, setMatches] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) { setQ(""); setAnswer(""); setMatches([]); }
  }, [open]);

  const ask = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setAnswer("");
    try {
      const { data } = await api.post("/nli", { query: q });
      setAnswer(data.answer);
      setMatches(data.matches || []);
    } catch (err) {
      setAnswer("Unable to answer right now. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="p-0 glass-strong border-0 max-w-2xl overflow-hidden">
        <form onSubmit={ask} className="p-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <Sparkle size={20} weight="duotone" className="text-[var(--accent)]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ask Clariva anything: 'Which contracts have the highest risk?' 'Show me all royalty obligations'…"
              data-testid="command-palette-input"
              className="flex-1 bg-transparent outline-none text-lg placeholder:text-[var(--text-3)]"
            />
            <kbd className="font-mono text-xs px-1.5 py-0.5 rounded bg-[var(--surface)] border border-[var(--border)]">ESC</kbd>
          </div>
        </form>

        <div className="max-h-[60vh] overflow-y-auto p-4 space-y-3">
          {loading && <div className="text-sm text-[var(--text-2)] animate-pulse">Clariva is thinking…</div>}
          {!loading && answer && (
            <div className="p-4 glass-subtle rounded-md">
              <div className="overline mb-2 flex items-center gap-2">
                <Sparkle size={12} weight="fill" /> AI answer
              </div>
              <div className="text-sm leading-relaxed">{answer}</div>
            </div>
          )}
          {matches.length > 0 && (
            <div>
              <div className="overline mb-2">Suggested contracts</div>
              <div className="space-y-1.5">
                {matches.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => { navigate(`/app/repository/${m.id}`); onClose?.(); }}
                    data-testid={`palette-match-${m.id}`}
                    className="w-full text-left p-3 rounded-md hover:bg-[var(--surface-2)] transition flex items-center gap-3 group"
                  >
                    <FileText size={18} className="text-[var(--text-2)]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{m.title}</div>
                      <div className="text-xs text-[var(--text-3)]">{m.counterparty}</div>
                    </div>
                    <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {!loading && !answer && (
            <div className="space-y-2">
              <div className="overline">Try</div>
              {[
                "Which contracts are high risk?",
                "List all upcoming renewal obligations",
                "Summarize the CloudMosaic SaaS agreement",
                "What's the total exposure with Harmonic Wave?",
              ].map((s) => (
                <button
                  key={s}
                  onClick={() => setQ(s)}
                  data-testid={`palette-suggest-${s.slice(0,20)}`}
                  className="w-full text-left p-3 rounded-md hover:bg-[var(--surface-2)] transition text-sm text-[var(--text-2)] hover:text-[var(--text)]"
                >
                  "{s}"
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
